import '../../App.css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';

const DOCS_URL = 'https://docs.schedpilot.com';

const linkStyle = { color: '#2563eb', textDecoration: 'underline', fontWeight: 500 };

// Each answer may include JSX (links, lists).
const FAQS = [
  {
    q: 'How do I grow my follower count by posting programmatically at certain hours?',
    a: (
      <>
        <p style={{ margin: '0 0 10px' }}>
          Growth comes from <strong>consistency</strong> and <strong>timing</strong>. The accounts that
          grow fastest publish on a steady cadence and post when their audience is most active.
        </p>
        <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
          <li>Use the <strong>Calendar</strong> and <strong>Schedule</strong> tools to queue posts ahead of time so you never miss a peak window.</li>
          <li>Check <strong>Analytics</strong> to find the hours and days your posts get the most impressions, then schedule into those slots.</li>
          <li>For full automation, post <strong>programmatically</strong> via the SchedPilot API or SDK on a cron job — your content goes out at the exact optimal hour, every day, without you lifting a finger.</li>
        </ul>
        <p style={{ margin: 0 }}>
          Learn more:{' '}
          <a href={`${DOCS_URL}/docs/guides/scheduling-logic`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Scheduling logic &amp; timezones</a>.
        </p>
      </>
    ),
  },
  {
    q: 'How do I use AI agents with the SchedPilot API?',
    a: (
      <>
        <p style={{ margin: '0 0 10px' }}>
          SchedPilot ships an official <strong>MCP server</strong> (<code>schedpilot-mcp</code>) and a
          developer REST API, so AI agents like Claude, Cursor, and Windsurf can manage your social
          posting for you.
        </p>
        <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
          <li>Create an API key in <strong>Settings → API access</strong>.</li>
          <li>Agents can list accounts, schedule posts (including X/Twitter threads), upload media, and pull analytics.</li>
          <li>Add the MCP with one command, e.g. in Claude Code:{' '}
            <code>claude mcp add schedpilot -s user -e SCHEDPILOT_TOKEN=smm_... -- npx -y schedpilot-mcp</code>.
          </li>
        </ul>
        <p style={{ margin: 0 }}>
          Learn more:{' '}
          <a href={`${DOCS_URL}/docs/ai-agents/mcp-tools`} target="_blank" rel="noopener noreferrer" style={linkStyle}>MCP tools reference</a>{' · '}
          <a href={`${DOCS_URL}/docs/api/create-post`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Create Post API</a>.
        </p>
      </>
    ),
  },
  {
    q: 'Which social networks can I post to through the API?',
    a: (
      <>
        <p style={{ margin: '0 0 10px' }}>
          The API works with every network SchedPilot supports — not just X / Twitter:
        </p>
        <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
          <li>X / Twitter, LinkedIn (profiles &amp; pages), Instagram, Facebook, Threads</li>
          <li>YouTube, TikTok, Pinterest, and Bluesky</li>
        </ul>
        <p style={{ margin: 0 }}>
          Call <code>GET /accounts</code> to list your connected accounts with their <code>id</code> and
          <code> type</code>, then pass those when creating a post. See the{' '}
          <a href={`${DOCS_URL}/docs/api/accounts`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Accounts reference</a>.
        </p>
      </>
    ),
  },
  {
    q: 'How do I publish to several networks at once via the API?',
    a: (
      <>
        <p style={{ margin: 0 }}>
          Send a single <code>POST /post</code> request and include every target in the <code>accounts</code> array —
          e.g. <code>{`[{ "id": 12, "type": "linkedin" }, { "id": 8, "type": "instagram" }, { "id": 3, "type": "youtube" }]`}</code>.
          One call schedules the same content across all of them (it also counts as just one write request against your rate limit). See the{' '}
          <a href={`${DOCS_URL}/docs/api/create-post`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Create Post reference</a>.
        </p>
      </>
    ),
  },
  {
    q: 'How do I attach images or video (YouTube, TikTok, Instagram, Facebook…) via the API?',
    a: (
      <>
        <p style={{ margin: '0 0 10px' }}>
          Upload the file first with <code>POST /media/upload</code> (from a public URL or raw bytes) — you get back a
          media id like <code>image-42</code> or <code>video-7</code>. Then pass it in the <code>attachments</code> array
          when you create the post. For video platforms like YouTube you can also set a <code>title</code>.
        </p>
        <p style={{ margin: 0 }}>
          See{' '}
          <a href={`${DOCS_URL}/docs/api/upload-media`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Upload Media</a>{' · '}
          <a href={`${DOCS_URL}/docs/api/create-post`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Create Post</a>.
        </p>
      </>
    ),
  },
  {
    q: 'Can I pull analytics for all my networks through the API?',
    a: (
      <>
        <p style={{ margin: 0 }}>
          Yes. <code>GET /analytics/{'{post_id}'}</code> returns impressions, reach, likes, comments, shares, and clicks
          for a published post, broken down per account/platform — so you can track performance across every network from
          one place. See the{' '}
          <a href={`${DOCS_URL}/docs/api/analytics`} target="_blank" rel="noopener noreferrer" style={linkStyle}>Analytics reference</a>.
        </p>
      </>
    ),
  },
  {
    q: 'How do I schedule a thread (multiple replies) on X / Twitter?',
    a: (
      <>
        <p style={{ margin: 0 }}>
          When you compose a post with an X / Twitter account selected, an <strong>X / Twitter thread</strong> box
          appears under the main text. Add one or more reply tweets — SchedPilot publishes the main tweet first,
          then posts each reply in order as a thread. You can do the same via the API with the <code>replies</code> field.
        </p>
      </>
    ),
  },
  {
    q: 'Where can I find the full documentation?',
    a: (
      <>
        <p style={{ margin: 0 }}>
          The complete developer and product documentation lives at{' '}
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" style={linkStyle}>docs.schedpilot.com</a> —
          including the REST API reference, authentication, rate limits, webhooks, and AI-agent guides.
        </p>
      </>
    ),
  },
];

function Faq() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0);

  return (
    <MainStructure activeItem="faq">
      <div style={{ maxWidth: 820 }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            Frequently asked questions
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#64748b', margin: 0 }}>
            Quick answers to the most common questions about scheduling, automation, and our API.
          </p>
        </div>

        {/* Documentation CTA */}
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12,
            padding: '14px 16px', marginBottom: 24,
          }}
        >
          <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.BookOpen size={18} color="#2563eb" />
          </div>
          <div style={{ flexGrow: 1 }}>
            <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>Read the documentation</p>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>Full API reference, guides, and AI-agent setup.</p>
          </div>
          <Icon.ExternalLink size={16} color="#2563eb" />
        </a>

        {/* Accordion */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, padding: '16px 18px', background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', fontSize: '0.95rem', fontWeight: 600, color: '#0f172a',
                  }}
                >
                  <span>{item.q}</span>
                  <Icon.ChevronDown
                    size={18}
                    color="#94a3b8"
                    style={{ flexShrink: 0, transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : 'none' }}
                  />
                </button>
                {isOpen && (
                  <div style={{ padding: '0 18px 18px', fontSize: '0.875rem', color: '#475569', lineHeight: 1.6 }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Back */}
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: 24, background: 'none', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '8px 14px', cursor: 'pointer', color: '#64748b', display: 'inline-flex',
            alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 500,
          }}
        >
          <Icon.ArrowLeft size={14} /> Back to dashboard
        </button>

      </div>
    </MainStructure>
  );
}

export default Faq;
